import { CloseButton, Loader, Text } from "@mantine/core";
import { Hourglass, Image, Video } from "lucide-react";

import { useGetTemplate } from "../../api/generated/endpoints/generation/generation";
import type { Job, TemplateCard as Template } from "../../api/generated/model";
import { FORMS } from "./forms";
import type { FormExample } from "./forms/kit";
import classes from "./templates.module.css";
import { PillCost } from "./TemplateCost";

export interface TemplatePanelProps {
  teamId: string;
  brandId: string;
  template: Template;
  /** Opened with Replicate: the form starts from the template's example. */
  replicate: boolean;
  /** The x on the pill: the only way to close the panel, as live's. */
  onClose: () => void;
  /** A run started: the page shows My Generations. */
  onStarted: (job: Job) => void;
}

/**
 * Live's form panel (`template-form-panel`): it floats at the page's foot over both tabs, live's
 * pill above it (the medium's icon, the template's title, an x). Under the pill, the template's
 * form (`forms/`), or, for a template whose form is not built, live's own words for a template
 * with no form. A card with an example reads its detail for the form's "Reuse example"; opened
 * with Replicate, the form waits for it, so it opens filled (one that cannot be read opens
 * empty, its switch off).
 */
export function TemplatePanel(props: TemplatePanelProps) {
  const { template, onClose } = props;
  const Form = FORMS[template.id];
  const built = Form !== undefined && template.available;
  const read = useGetTemplate(props.teamId, template.id, {
    query: { enabled: built && template.has_example },
  });
  const detail = read.data;
  const failed = read.isError;
  const example: FormExample | null =
    detail?.example === null || detail?.example === undefined
      ? null
      : { inputs: detail.example, urls: detail.example_input_urls, voices: detail.example_voices ?? [] };
  const Icon = template.media === "video" ? Video : Image;
  const pill = (
    <div className={classes.panelPills}>
      <span className={classes.panelPill}>
        <Icon size={20} strokeWidth={1.5} aria-hidden />
        <span className={classes.panelTitle}>{template.title}</span>
        <CloseButton size="sm" aria-label="Close the template" onClick={onClose} />
      </span>
      {/* Live's detailed cost: the tier bolts and the range chip (G14-template-costs). */}
      {template.estimate === null || template.estimate === undefined ? null : <PillCost estimate={template.estimate} />}
    </div>
  );
  if (built && props.replicate && template.has_example && detail === undefined && !failed) {
    return (
      <section className={classes.panel} aria-label={template.title}>
        {pill}
        <div className={classes.surface}>
          <Loader size="sm" aria-label="Loading the example" />
        </div>
      </section>
    );
  }
  if (built) {
    return (
      <section aria-label={template.title}>
        <Form
          teamId={props.teamId}
          brandId={props.brandId}
          template={template}
          above={pill}
          replicate={props.replicate}
          example={example}
          options={detail?.options ?? []}
          onStarted={props.onStarted}
        />
      </section>
    );
  }
  return (
    <section className={classes.panel} aria-label={template.title}>
      {pill}
      <div className={classes.surface}>
        <Hourglass size={24} strokeWidth={1.5} aria-hidden />
        <Text className={classes.surfaceTitle}>This template is not available yet.</Text>
        <Text className={classes.surfaceText}>Its widget is coming soon.</Text>
      </div>
    </section>
  );
}
